"use client";

import { ComponentType, ReactNode } from "react";

export interface ProviderConfigWithProps<P = any> {
  Component: ComponentType<P>;
  props?: P;
}

export type ProviderConfig<P = any> =
  | ComponentType<P>
  | ProviderConfigWithProps<P>;

interface ProviderComposerProps {
  providers: ProviderConfig[];
  children: ReactNode;
}

/**
 * Composes multiple providers into a single component, reducing nesting.
 * Providers are composed in order: first provider is outermost, last is innermost.
 *
 * @example
 * ```tsx
 * <ProviderComposer
 *   providers={[
 *     { Component: Auth0Provider, props: { domain: "...", clientId: "..." } },
 *     AuthContextProvider,
 *     NextUIProvider,
 *   ]}
 * >
 *   {children}
 * </ProviderComposer>
 * ```
 */
export default function ProviderComposer({
  providers,
  children,
}: ProviderComposerProps) {
  // Compose providers from innermost to outermost (reverse order)
  // This ensures the first provider in the array is the outermost wrapper
  return providers.reduceRight((acc, provider) => {
    // Check if provider is a component directly or an object with Component and props
    if (typeof provider === "function") {
      const Component = provider;
      return <Component>{acc}</Component>;
    } else {
      const { Component, props = {} } = provider;
      return <Component {...props}>{acc}</Component>;
    }
  }, children as ReactNode);
}

